import { toast } from 'sonner'
import { useEffect, useState, Suspense, lazy } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { api } from '@/lib/api'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { EmptyState } from '@/components/shared/EmptyState'
import type { OutboundMessage } from '@/types'
import { Send, ChevronLeft, ChevronRight, Plus, Zap, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

const ComposeEmail = lazy(() => import('@/components/shared/ComposeEmail').then((m) => ({ default: m.ComposeEmail })))

export function SendHistory() {
  const [messages, setMessages] = useState<OutboundMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [composeOpen, setComposeOpen] = useState(false)
  const [rateLimit, setRateLimit] = useState<{ remaining: number; limit: number } | null>(null)

  const fetchMessages = async () => {
    try {
      const res = await api.get(`/outbound/messages?page=${page}&per_page=20`)
      setMessages(res.data.messages || [])
      setTotal(res.data.pagination?.total || 0)
      setTotalPages(res.data.pagination?.total_pages || 1)
      const remaining = res.headers['x-emails-remaining-today']
      const limit = res.headers['x-emails-limit']
      if (remaining && limit) {
        setRateLimit({ remaining: parseInt(remaining), limit: parseInt(limit) })
      }
    } catch {
      toast.error('Failed to load send history')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMessages()
  }, [page])

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 w-32 bg-accent rounded" />
        <div className="h-64 bg-accent rounded-xl" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Send History</h1>
          <p className="text-sm text-muted-foreground mt-1">Outbound email activity.</p>
        </div>
        <div className="flex items-center gap-3">
          {rateLimit && (
            <div className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-accent/50 border border-border">
              <Zap className="h-3 w-3 text-amber-400" />
              <span className="text-muted-foreground">{rateLimit.remaining.toLocaleString()}</span>
              <span className="text-muted-foreground/60">/ {rateLimit.limit.toLocaleString()} today</span>
            </div>
          )}
          <Button onClick={() => setComposeOpen(true)} className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700">
            <Plus className="mr-2 h-4 w-4" />
            Compose
          </Button>
        </div>
      </div>

      {messages.length === 0 ? (
        <EmptyState
          title="No sends yet"
          description="Emails sent via API will appear here."
          icon={<Send className="h-5 w-5" />}
        />
      ) : (
        <>
          <Card>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {messages.map((msg) => (
                  <div key={msg.id} className="flex items-center justify-between px-4 py-3 hover:bg-accent/20 transition-colors">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{msg.subject}</p>
                      <p className="text-xs text-muted-foreground">From: {msg.from_email} → {msg.to?.map((r: any) => typeof r === 'string' ? r : r.email || r).join(', ')}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <StatusBadge status={msg.status} />
                      {msg.sent_at && (
                        <span className="text-xs text-muted-foreground hidden sm:inline">
                          {new Date(msg.sent_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">{total} total</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">Page {page}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}
      {composeOpen && (
        <Suspense fallback={
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        }>
          <ComposeEmail open={composeOpen} onClose={() => setComposeOpen(false)} onSent={fetchMessages} />
        </Suspense>
      )}
    </div>
  )
}
